// Document status per type: what a stored status means for each document type, its chip and the
// manual changes allowed. The DB keeps one enum (PROBLEM, RECEIVED, IN_PROGRESS, PRINTED, PAID, ARCHIVED);
// „Abgeglichen“ is derived (confirmed statement match). Transitions come from @rimon/contracts, which the
// backend validates too (422 invalid_status_transition).
//
// Chip texts are the German UI words (the design mixes in the French Notion statuses Reçu / Imprimé / Payé);
// the French UI translates them through the word pairs, `fr` is there for code that needs it directly.
import { DOCUMENT_STATUS_TRANSITIONS, PAYABLE_DOCUMENT_TYPES, type DocumentStatusContract, type DocumentTypeContract } from '@rimon/contracts';
import { isFr } from '../i18n/lang';

export type DocStatus = DocumentStatusContract;
export type DocType = DocumentTypeContract;
/** Stored status or the derived „reconciled“ (a confirmed statement match). */
export type StatusKey = DocStatus | 'RECONCILED';

export interface StatusView { key: StatusKey; de: string; fr: string; bg: string; fg: string }

// Chip colours of the design (logic.ts chip()).
const C = {
  blue: ['rgba(19,117,189,.2)', '#8CC0EA'], amber: ['rgba(198,138,30,.2)', '#E6B55C'], red: ['rgba(229,115,115,.16)', '#E57373'],
  grey: ['rgba(255,255,255,.1)', '#DCD9DA'], green: ['rgba(5,167,157,.18)', '#6FD3CB'], dim: ['rgba(255,255,255,.06)', '#9B9698'],
} as const;
const v = (de: string, fr: string, c: readonly [string, string]) => ({ de, fr, bg: c[0], fg: c[1] });

const PROBLEM = v('Problème', 'Problème', C.red), ARCHIVED = v('Archiviert', 'Archivé', C.dim), RECONCILED = v('Abgeglichen', 'Rapproché', C.green);

/** Per type only the statuses that make sense there; anything else falls back to the generic label. */
export const STATUS_MODEL: Record<DocType, Partial<Record<StatusKey, Omit<StatusView, 'key'>>>> = {
  INCOMING_INVOICE: { RECEIVED: v('Reçu', 'Reçu', C.blue), PROBLEM, IN_PROGRESS: v('In Vorbereitung', 'En préparation', C.amber), PRINTED: v('Imprimé', 'Imprimé', C.grey), PAID: v('Payé', 'Payé', C.green), RECONCILED, ARCHIVED },
  TAX_OR_SOCIAL_CHARGE: { RECEIVED: v('Reçu', 'Reçu', C.blue), PROBLEM, PRINTED: v('Imprimé', 'Imprimé', C.grey), PAID: v('Payé', 'Payé', C.green), RECONCILED, ARCHIVED },
  OUTGOING_INVOICE: { RECEIVED: v('Émise', 'Émise', C.blue), PROBLEM, PAID: v('Encaissée', 'Encaissée', C.green), RECONCILED, ARCHIVED },
  OUTGOING_CREDIT_NOTE: { RECEIVED: v('Émise', 'Émis', C.blue), PROBLEM, PAID: v('Verrechnet', 'Imputé', C.green), RECONCILED, ARCHIVED },
  PAYROLL: { RECEIVED: v('Reçue', 'Reçue', C.blue), PROBLEM, PAID: v('Payée', 'Payée', C.green), RECONCILED, ARCHIVED },
  RECEIPT: { RECEIVED: v('Reçu', 'Reçu', C.blue), PROBLEM, PAID: v('Payé', 'Payé', C.green), RECONCILED, ARCHIVED },
  CREDIT_NOTE: { RECEIVED: v('Ouverte', 'Ouverte', C.amber), PROBLEM, PAID: v('Verrechnet', 'Utilisée', C.green), ARCHIVED },
  // Extraits rows are Statements: open until the month is closed (ARCHIVED = closed).
  BANK_STATEMENT: { RECEIVED: v('Offen', 'Ouvert', C.amber), IN_PROGRESS: v('Offen', 'Ouvert', C.amber), PROBLEM, ARCHIVED: v('Abgeschlossen', 'Terminé', C.green) },
};

const GENERIC: Record<DocStatus, Omit<StatusView, 'key'>> = {
  RECEIVED: v('Reçu', 'Reçu', C.blue), PROBLEM, IN_PROGRESS: v('In Vorbereitung', 'En préparation', C.amber), PRINTED: v('Imprimé', 'Imprimé', C.grey), PAID: v('Payé', 'Payé', C.green), ARCHIVED,
};

/** Chip of a document. `reconciled` (confirmed statement match) wins over PAID for the types that can be matched. */
export function statusView(type: DocType, status: DocStatus, reconciled = false): StatusView {
  const m = STATUS_MODEL[type] || {};
  if (reconciled && status !== 'ARCHIVED' && m.RECONCILED) return { key: 'RECONCILED', ...m.RECONCILED };
  return { key: status, ...(m[status] || GENERIC[status] || GENERIC.RECEIVED) };
}

/** Label in the current UI language. */
export const statusText = (type: DocType, status: DocStatus, reconciled = false) => { const s = statusView(type, status, reconciled); return isFr() ? s.fr : s.de; };

/** Statuses a user may set by hand from `status` (same rules as the backend). */
export function nextStatuses(type: DocType, status: DocStatus): StatusView[] {
  return (DOCUMENT_STATUS_TRANSITIONS[type]?.[status] || []).map((to) => statusView(type, to));
}
export const canSetStatus = (type: DocType, from: DocStatus, to: DocStatus) => !!DOCUMENT_STATUS_TRANSITIONS[type]?.[from]?.includes(to);

/** Types that show up under Bezahlung (never outgoing invoices). */
export const isPayable = (type: DocType) => (PAYABLE_DOCUMENT_TYPES as readonly string[]).includes(type);

/** Colours by chip text, for rows that only carry a label (mock data, payment rows). */
export function chipByLabel(label: string): [string, string] | undefined {
  for (const t of Object.values(STATUS_MODEL)) for (const s of Object.values(t)) if (s && s.de === label) return [s.bg, s.fg];
  return undefined;
}
